import { DEFAULT_CONFIGS, SURFACES } from '../data/designVariants'
import { readPayload, wireSurface, writeConfig, type WirePayload } from './designVariantWire'
import { HAS_API, requestJson } from './apiConfig'
import type {
  DesignVariantChange,
  DesignVariantConfig,
  SurfaceKey,
} from '../types/experience'

/**
 * The design variant configuration service.
 *
 * This is the ONE seam between the product and where design configuration
 * actually lives. Everything above it is written against these functions, so
 * nothing in the interface reads or writes a configuration store directly and
 * no component hard-codes an interval or a design.
 *
 * The contract, which the client speaks whenever an API origin is configured:
 *
 *   GET  /api/config/design-variants                  → published configuration (public read)
 *   PUT  /api/admin/design-variants/{surface}          → publish (permission enforced server-side)
 *   GET  /api/admin/design-variants/history            → published change history
 *
 * When no API origin is configured there is no configuration server to talk
 * to. The record is then held in this browser, and every read reports that
 * through `source`, so the console can label it a local draft rather than
 * present it as production state. Nothing silently pretends to be live.
 *
 * Every function is async and every read has a defined failure result, because
 * the calling code must behave correctly when configuration is unavailable —
 * the surface falls back to its fallback design, never to a blank page.
 */

const CONFIG_KEY = 'campuzon.design.config'
const HISTORY_KEY = 'campuzon.design.history'

const PUBLIC_PATH = '/api/config/design-variants'
const ADMIN_PATH = '/api/admin/design-variants'

export type ConfigMap = Record<SurfaceKey, DesignVariantConfig>

/** Where the configuration currently in use actually came from. */
export type ConfigSource = 'server' | 'local' | 'defaults'

export interface ConfigResult {
  configs: ConfigMap
  source: ConfigSource
  /** Set when the server was configured but could not be reached. */
  error: string | null
}

/** True when a configuration server is configured and being used. */
export const CONFIG_IS_SERVER_BACKED = HAS_API
/** Kept for callers that ask the inverse question. */
export const CONFIG_IS_LOCAL = !HAS_API

/**
 * A payload from the configuration service arrives in the published wire
 * format (surface ids `homepage` / `housing` / `discover`, durations in
 * seconds). Translation happens in one place so nothing else parses it.
 */
function fromWire(payload: WirePayload | null | undefined): Partial<ConfigMap> {
  return readPayload(payload) as Partial<ConfigMap>
}

function isValid(config: unknown): config is DesignVariantConfig {
  if (!config || typeof config !== 'object') return false
  const value = config as DesignVariantConfig
  if (!SURFACES.some((surface) => surface.key === value.surface)) return false
  if (!['DESIGN_1', 'DESIGN_2', 'ROTATE'].includes(value.mode)) return false
  if (typeof value.rotationIntervalMs !== 'number' || value.rotationIntervalMs < 1000) return false
  // A surface with both designs withdrawn has nothing to present. Rather than
  // render nothing, the record is treated as unusable and defaults are used.
  if (!value.design1Enabled && !value.design2Enabled) return false
  return true
}

/** Only records the client recognises are accepted, wherever they came from. */
function merge(candidates: Partial<ConfigMap> | null | undefined): ConfigMap {
  const merged: ConfigMap = { ...DEFAULT_CONFIGS }
  if (!candidates) return merged
  for (const surface of SURFACES) {
    const candidate = candidates[surface.key]
    if (isValid(candidate)) merged[surface.key] = candidate
  }
  return merged
}

function readLocal(): Partial<ConfigMap> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(CONFIG_KEY)
    return raw ? (JSON.parse(raw) as Partial<ConfigMap>) : null
  } catch {
    return null
  }
}

function writeLocal(configs: ConfigMap): void {
  try {
    window.localStorage.setItem(CONFIG_KEY, JSON.stringify(configs))
  } catch {
    /* Storage being unavailable is not a reason to fail a publish path. */
  }
}

export async function load(): Promise<ConfigResult> {
  if (typeof window === 'undefined') {
    return { configs: { ...DEFAULT_CONFIGS }, source: 'defaults', error: null }
  }

  if (HAS_API) {
    try {
      const payload = await requestJson<WirePayload>(PUBLIC_PATH)
      const configs = merge(fromWire(payload))
      // Cached so a later network failure degrades to the last known published
      // configuration rather than jumping back to the platform defaults.
      writeLocal(configs)
      return { configs, source: 'server', error: null }
    } catch (failure) {
      const cached = readLocal()
      return {
        configs: merge(cached),
        source: cached ? 'local' : 'defaults',
        error:
          failure instanceof Error
            ? `${failure.message} The last configuration read is in use.`
            : 'The configuration service could not be reached.',
      }
    }
  }

  const stored = readLocal()
  return { configs: merge(stored), source: stored ? 'local' : 'defaults', error: null }
}

export async function publish(
  next: DesignVariantConfig,
  change: DesignVariantChange,
): Promise<ConfigMap> {
  if (!isValid(next)) {
    throw new Error('That configuration would leave the surface with nothing to present.')
  }

  if (HAS_API) {
    // The server is authoritative: it enforces the permission, stamps the
    // record and writes the audit entry. The client renders what comes back.
    const payload = await requestJson<WirePayload>(`${ADMIN_PATH}/${wireSurface(next.surface)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      // The server validates, authorises, stamps, versions and audits. The
      // reason travels with the change so the audit entry can explain itself.
      body: JSON.stringify({ ...writeConfig(next), reason: change.reason }),
    })
    const configs = merge(fromWire(payload))
    writeLocal(configs)
    return configs
  }

  const current = await load()
  const merged: ConfigMap = { ...current.configs, [next.surface]: next }
  try {
    window.localStorage.setItem(CONFIG_KEY, JSON.stringify(merged))
    const past = await history()
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify([change, ...past].slice(0, 50)))
  } catch {
    throw new Error('The configuration could not be saved. Nothing has changed for visitors.')
  }
  return merged
}

export async function history(): Promise<DesignVariantChange[]> {
  if (typeof window === 'undefined') return []

  if (HAS_API) {
    try {
      return await requestJson<DesignVariantChange[]>(`${ADMIN_PATH}/history`)
    } catch {
      return []
    }
  }

  try {
    const raw = window.localStorage.getItem(HISTORY_KEY)
    return raw ? (JSON.parse(raw) as DesignVariantChange[]) : []
  } catch {
    return []
  }
}

/** How often an open session re-reads the published configuration. */
export const REVALIDATE_MS = 60_000

/** Broadcast key so an open tab picks up a publish without a reload. */
export const CONFIG_STORAGE_KEY = CONFIG_KEY
